import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  useMediaQuery,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  alpha
} from '@mui/material';

import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import NotificationBell from './NotificationBell';
import { useAuth } from '../contexts/AuthContext';

const AdminLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/sign-in');
    handleMenuClose();
  };

  const handleProfile = () => {
    navigate('/admin/profile');
    handleMenuClose();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      
      {/* ================= MOBILE APP BAR ================= */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: 'transparent',
            borderBottom: 'none',
            boxShadow: 'none',
          }}
        >
          <Toolbar
            sx={{
              minHeight: 64,
              px: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {/* LEFT: PROFILE */}
            <Tooltip title="Account">
              <IconButton
                onClick={handleMenuOpen}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.common.white, 0.9),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 1),
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: theme.palette.primary.main,
                  }}
                >
                  {currentUser?.displayName?.[0] ||
                    currentUser?.email?.[0]}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* RIGHT: NOTIFICATION */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationBell />
            </Box>

            {/* MENU */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: 2,
                  boxShadow: theme.shadows[3],
                },
              }}
            >
              <MenuItem onClick={handleProfile}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
      )}

      {/* ================= SIDEBAR ================= */}
      <Sidebar
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        variant={isDesktop ? 'permanent' : 'temporary'}
      />

      {/* ================= MAIN CONTENT ================= */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: '100%', lg: 'auto' },
          ml: { lg: '0px' },
          mt: { xs: '64px', lg: 0 },
          mb: { xs: '65px', lg: 0 },
          transition: theme.transitions.create(['margin'], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.sharp,
          }),
        }}
      >
        <Outlet />
      </Box>

      {/* ================= BOTTOM NAV (MOBILE) ================= */}
      {isMobile && <BottomNav />}
    </Box>
  );
};

export default AdminLayout;